import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  inviteUser, 
  getUserInvites, 
  cancelInvite, 
  resendInvite,
  getUsers,
  updateUser,
  deleteUser,
  getOrganizations 
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus,
  Shield,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  RefreshCw,
  Trash2,
  Building2,
  Edit,
  UserCog
} from 'lucide-react';

const USER_ROLES = [
  { value: 'super_admin', label: 'Super Admin', icon: Shield, description: 'Full platform access' },
  { value: 'org_admin', label: 'Organization Admin', icon: Building2, description: 'Manage a specific organization' },
  { value: 'broker', label: 'Broker / Producer', icon: Users, description: 'Standalone Players Health user — manages prospects & intake across many organizations' },
];

// Roles that are standalone Players Health users (NOT tied to a single organization)
const STANDALONE_ROLES = new Set(['super_admin', 'broker']);

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending' },
  accepted: { icon: CheckCircle, color: 'bg-green-100 text-green-800 border-green-300', label: 'Accepted' },
  expired: { icon: XCircle, color: 'bg-red-100 text-red-800 border-red-300', label: 'Expired' },
  cancelled: { icon: XCircle, color: 'bg-gray-100 text-gray-800 border-gray-300', label: 'Cancelled' },
};

export default function UserManagement() {
  const { user, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('users');
  const [inviteTab, setInviteTab] = useState('all');
  
  // Dialog states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'org_admin',
    organization_id: '',
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    role: '',
    organization_id: '',
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'org_admin',
      organization_id: '',
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, invitesRes, orgsRes] = await Promise.all([
        getUsers(),
        getUserInvites(),
        isSuperAdmin() ? getOrganizations() : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in Name and Email');
      return;
    }

    if (!STANDALONE_ROLES.has(formData.role) && !formData.organization_id) {
      toast.error('Please select an organization for this role');
      return;
    }

    setSaving(true);
    try {
      const inviteData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        organization_id: STANDALONE_ROLES.has(formData.role) ? null : formData.organization_id,
      };
      
      const res = await inviteUser(inviteData);
      toast.success(`Invitation sent to ${formData.email}`, {
        description: res.data.email_sent ? 'Email notification sent' : 'Email notification not configured'
      });
      setShowInviteDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setEditFormData({
      name: userToEdit.name || '',
      role: userToEdit.role || 'org_admin',
      organization_id: userToEdit.organization_id || '',
      is_active: userToEdit.is_active !== false,
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    if (!STANDALONE_ROLES.has(editFormData.role) && !editFormData.organization_id) {
      toast.error('Please select an organization for this role');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: editFormData.name,
        role: editFormData.role,
        is_active: editFormData.is_active,
        organization_id: STANDALONE_ROLES.has(editFormData.role) ? null : editFormData.organization_id,
      };
      await updateUser(selectedUser.id, payload);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setDeleting(true);
    try {
      await deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await cancelInvite(inviteId);
      toast.success('Invite cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleResendInvite = async (inviteId) => {
    setResending(inviteId);
    try {
      const res = await resendInvite(inviteId);
      toast.success('Invite resent', {
        description: res.data.email_sent ? 'Email notification sent' : 'Expiration extended'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to resend invite');
    } finally {
      setResending(null);
    }
  };

  // Filter invites by status
  const filteredInvites = inviteTab === 'all' 
    ? invites 
    : invites.filter(i => i.status === inviteTab);

  // Counts
  const pendingCount = invites.filter(i => i.status === 'pending').length;
  const acceptedCount = invites.filter(i => i.status === 'accepted').length;
  const totalUsers = users.length;
  const superAdminCount = users.filter(u => u.role === 'super_admin').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="user-management-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage platform users and invites
          </p>
        </div>
        
        <Button 
          className="btn-primary-glow" 
          onClick={() => {
            resetForm();
            setShowInviteDialog(true);
          }}
          data-testid="invite-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalUsers}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-sm text-muted-foreground">Pending Invites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{acceptedCount}</p>
                <p className="text-sm text-muted-foreground">Accepted Invites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{superAdminCount}</p>
                <p className="text-sm text-muted-foreground">Super Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs: Users vs Invites */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={mainTab} onValueChange={setMainTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="users" data-testid="tab-users">
                <UserCog className="w-4 h-4 mr-2" />
                Users ({totalUsers})
              </TabsTrigger>
              <TabsTrigger value="invites" data-testid="tab-invites">
                <Mail className="w-4 h-4 mr-2" />
                Invites ({invites.length})
              </TabsTrigger>
            </TabsList>

            {/* Users Tab */}
            <TabsContent value="users">
              {users.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No users found</p>
                  <Button variant="link" onClick={() => setShowInviteDialog(true)}>
                    Invite your first user
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => {
                      const roleConfig = USER_ROLES.find(r => r.value === u.role);
                      const isActive = u.is_active !== false;
                      
                      return (
                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{u.name}</p>
                              <p className="text-sm text-muted-foreground">{u.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {roleConfig?.label || u.role?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {u.organization_name || (STANDALONE_ROLES.has(u.role) ? 'Platform-wide' : '-')}
                          </TableCell>
                          <TableCell>
                            <Badge className={isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenEditDialog(u)}
                                data-testid={`edit-user-${u.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {isSuperAdmin() && u.id !== user?.id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:bg-destructive/10"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowDeleteDialog(true);
                                  }}
                                  data-testid={`delete-user-${u.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Invites Tab */}
            <TabsContent value="invites">
              <Tabs value={inviteTab} onValueChange={setInviteTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="all">All ({invites.length})</TabsTrigger>
                  <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
                  <TabsTrigger value="accepted">Accepted ({acceptedCount})</TabsTrigger>
                </TabsList>

                <TabsContent value={inviteTab}>
                  {filteredInvites.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No invites found</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Invited By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredInvites.map((invite) => {
                          const statusConfig = STATUS_CONFIG[invite.status] || STATUS_CONFIG.pending;
                          const StatusIcon = statusConfig.icon;
                          const roleConfig = USER_ROLES.find(r => r.value === invite.role);
                          
                          return (
                            <TableRow key={invite.id} data-testid={`invite-row-${invite.id}`}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{invite.name}</p>
                                  <p className="text-sm text-muted-foreground">{invite.email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">
                                  {roleConfig?.label || invite.role?.replace('_', ' ')}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {invite.organization_name || (STANDALONE_ROLES.has(invite.role) ? 'Platform-wide' : '-')}
                              </TableCell>
                              <TableCell>
                                <Badge className={statusConfig.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {invite.invited_by_name || '-'}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {new Date(invite.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                {invite.status === 'pending' && (
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleResendInvite(invite.id)}
                                      disabled={resending === invite.id}
                                      data-testid={`resend-invite-${invite.id}`}
                                    >
                                      {resending === invite.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="w-4 h-4" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive hover:bg-destructive/10"
                                      onClick={() => handleCancelInvite(invite.id)}
                                      data-testid={`cancel-invite-${invite.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invite User
            </DialogTitle>
            <DialogDescription>
              Send an email invitation to join the platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteUser} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="role">Role *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="w-4 h-4" />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {USER_ROLES.find(r => r.value === formData.role)?.description}
              </p>
            </div>
            
            {!STANDALONE_ROLES.has(formData.role) && (
              <div>
                <Label htmlFor="organization">Organization *</Label>
                <Select 
                  value={formData.organization_id} 
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="invite-org-select">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {STANDALONE_ROLES.has(formData.role) && formData.role === 'broker' && (
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-sm text-muted-foreground">
                <strong>Standalone Player's Health user.</strong> Brokers are not tied to a specific organization. After accepting the invite, they can create intake links and manage their full book of business across many organizations.
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="send-invite-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invite
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit User
            </DialogTitle>
            <DialogDescription>
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={selectedUser?.email || ''} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select 
                value={editFormData.role} 
                onValueChange={(value) => setEditFormData({ 
                  ...editFormData, 
                  role: value, 
                  organization_id: STANDALONE_ROLES.has(value) ? '' : editFormData.organization_id 
                })}
              >
                <SelectTrigger data-testid="edit-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <role.icon className="w-4 h-4" />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {USER_ROLES.find(r => r.value === editFormData.role)?.description}
              </p>
            </div>
            
            {!STANDALONE_ROLES.has(editFormData.role) && (
              <div>
                <Label htmlFor="edit-organization">Organization *</Label>
                <Select 
                  value={editFormData.organization_id} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, organization_id: value })}
                >
                  <SelectTrigger data-testid="edit-org-select">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editFormData.role === 'super_admin' && (
              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                Super Admins have platform-wide access and are not tied to any organization.
              </p>
            )}

            {editFormData.role === 'broker' && (
              <p className="text-sm text-muted-foreground bg-blue-500/5 border border-blue-500/30 p-3 rounded-md">
                Brokers are standalone Players Health users. They manage their full book of business across many organizations via intake links — they are not tied to any single organization.
              </p>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-active"
                checked={editFormData.is_active}
                onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="edit-active" className="cursor-pointer">Active User</Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
